import {
  AttachmentChip,
  AttachmentIcon,
} from '@/components/AgentChat/AttachmentChip';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { PromptAttachment } from '@/types/agentChat';
import { getAdsPlatformIcon } from '@/utils/assets';
import { ScrollArea, ScrollBar } from '@superfunnel/ui-core/v2/ScrollArea';
import { FrameSelect, MediaImage } from 'iconoir-react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

/** Simple inline Figma logo SVG for the attachment chip */
function FigmaIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 38 57"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z"
        fill="#1ABCFE"
      />
      <path
        d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z"
        fill="#0ACF83"
      />
      <path
        d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z"
        fill="#FF7262"
      />
      <path
        d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z"
        fill="#F24E1E"
      />
      <path
        d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z"
        fill="#A259FF"
      />
    </svg>
  );
}

interface IFilePreview {
  size?: 'sm' | 'lg';
  promptContexts: PromptAttachment[];
  setPromptContexts: Dispatch<SetStateAction<PromptAttachment[]>>;
}

export const ChatInputContextsList = ({
  size = 'sm',
  promptContexts,
  setPromptContexts,
}: IFilePreview) => {
  const { track } = useAnalytics();
  const [filePreviews, setFilePreviews] = useState<
    Record<string, { name: string; src: string; type: string }>
  >({});

  // Generate file previews for file contexts
  useEffect(() => {
    const fileContexts = promptContexts.filter((ctx) => ctx.type === 'file');

    if (!fileContexts.length) {
      setFilePreviews({});
      return;
    }

    const readers: FileReader[] = [];
    const newPreviews: Record<
      string,
      { name: string; src: string; type: string }
    > = {};

    fileContexts.forEach((ctx) => {
      if (ctx.type !== 'file') return;
      const file = ctx.localFile;
      if (!file) {
        // If there's no localFile, use the uploaded URL if available
        if (ctx.uploadState?.uploadedUrl) {
          newPreviews[ctx.id] = {
            name: ctx.name || 'File',
            src: ctx.uploadState.uploadedUrl,
            type: ctx.mimeType || 'application/octet-stream',
          };
          if (Object.keys(newPreviews).length === fileContexts.length) {
            setFilePreviews(newPreviews);
          }
        }
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        newPreviews[ctx.id] = {
          name: file.name,
          src: e.target?.result as string,
          type: file.type,
        };

        if (Object.keys(newPreviews).length === fileContexts.length) {
          setFilePreviews(newPreviews);
        }
      };

      reader.readAsDataURL(file);
      readers.push(reader);
    });

    return () => {
      readers.forEach((r) => r.abort());
    };
  }, [promptContexts]);

  const renderContextItem = (ctx: PromptAttachment) => {
    switch (ctx.type) {
      case 'campaign': {
        return (
          <AttachmentChip
            key={ctx.id}
            size={size}
            label={ctx.name}
            onDelete={() => {
              track('chat_input_context_removed', {
                context_type: 'campaign',
                context_id: ctx.id,
                campaign_name: ctx.name,
                campaign_type: ctx.provider,
                total_contexts: promptContexts.length - 1,
              });
              setPromptContexts((prev) =>
                prev.filter((prevCtx) => prevCtx.id !== ctx.id),
              );
            }}
            icon={
              <img
                src={getAdsPlatformIcon(ctx.provider)}
                alt={''}
                className={'ml-0.5 block size-6'}
              />
            }
          />
        );
      }

      case 'file': {
        const localFile = ctx.localFile;
        const uploadState = ctx.uploadState;
        const isUploading = uploadState?.isUploading || false;
        const progress = uploadState?.progress || 0;
        const hasError = !!uploadState?.error;
        const preview = filePreviews[ctx.id];
        const fileName = ctx.name || localFile?.name || 'File';
        const fileType = ctx.mimeType || localFile?.type || '';
        const fileSize = ctx.size || localFile?.size || 0;

        return (
          <AttachmentChip
            key={ctx.id}
            size={size}
            label={fileName}
            isUploading={isUploading}
            progress={progress}
            hasError={hasError}
            onDelete={() => {
              track('chat_input_context_removed', {
                context_type: 'file',
                context_id: ctx.id,
                file_name: fileName,
                file_type: fileType,
                file_size: fileSize,
                had_error: hasError,
                total_contexts: promptContexts.length - 1,
              });
              setPromptContexts((prev) =>
                prev.filter((prevCtx) => prevCtx.id !== ctx.id),
              );
            }}
            icon={
              preview ? (
                <AttachmentIcon
                  mimeType={preview.type}
                  src={preview.src}
                  name={preview.name}
                />
              ) : (
                // Fallback while preview is loading
                <div className="flex size-7.5 items-center justify-center rounded-lg bg-gray-200">
                  <span className="text-xs text-gray-500">...</span>
                </div>
              )
            }
          />
        );
      }

      case 'element': {
        const displayText = ctx.elementText
          ? ctx.elementText.substring(0, 30) +
            (ctx.elementText.length > 30 ? '...' : '')
          : ctx.elementTag
            ? `<${ctx.elementTag}>`
            : 'Element';

        return (
          <AttachmentChip
            key={ctx.id}
            size={size}
            label={displayText}
            onDelete={() => {
              track('chat_input_context_removed', {
                context_type: 'element',
                context_id: ctx.id,
                element_tag: ctx.elementTag,
                has_text: !!ctx.elementText,
                text_length: ctx.elementText?.length || 0,
                total_contexts: promptContexts.length - 1,
              });
              setPromptContexts((prev) =>
                prev.filter((prevCtx) => prevCtx.id !== ctx.id),
              );
            }}
            icon={<FrameSelect className="text-base-500 ms-1 size-4" />}
          />
        );
      }

      case 'asset': {
        const assetName = ctx.asset.alt || 'Image asset';
        const thumbnailUrl = ctx.asset.thumbnail || ctx.asset.url;
        return (
          <AttachmentChip
            key={ctx.id}
            size={size}
            label={assetName}
            onDelete={() => {
              track('chat_input_context_removed', {
                context_type: 'asset',
                context_id: ctx.id,
                asset_name: assetName,
                total_contexts: promptContexts.length - 1,
              });
              setPromptContexts((prev) =>
                prev.filter((prevCtx) => prevCtx.id !== ctx.id),
              );
            }}
            icon={
              thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={assetName}
                  className="size-7.5 rounded-lg object-cover"
                />
              ) : (
                <MediaImage className="text-base-500 ms-1 size-4" />
              )
            }
          />
        );
      }

      case 'figma': {
        const figmaName = ctx.name || 'Figma Export';
        const isUploading = ctx.uploadState?.isUploading || false;
        const progress = ctx.uploadState?.progress || 0;
        const hasError = !!ctx.uploadState?.error;
        // Use the screenshot data URI as thumbnail while uploading,
        // or the uploaded screenshot URL after upload
        const thumbnailSrc = ctx.screenshotDataUri || ctx.screenshotUrl;

        return (
          <AttachmentChip
            key={ctx.id}
            size={size}
            label={figmaName}
            isUploading={isUploading}
            progress={progress}
            hasError={hasError}
            onDelete={() => {
              track('chat_input_context_removed', {
                context_type: 'figma',
                context_id: ctx.id,
                figma_name: figmaName,
                total_contexts: promptContexts.length - 1,
              });
              setPromptContexts((prev) =>
                prev.filter((prevCtx) => prevCtx.id !== ctx.id),
              );
            }}
            icon={
              thumbnailSrc ? (
                <img
                  src={thumbnailSrc}
                  alt={figmaName}
                  className="size-7.5 rounded-lg object-cover"
                />
              ) : (
                <FigmaIcon className="ms-1 size-4" />
              )
            }
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <ScrollArea className="relative mb-1.5 w-full rounded-md whitespace-nowrap">
      <div className={'flex items-center gap-2'}>
        {promptContexts.map(renderContextItem)}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};
